import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, ImagePlus, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE, ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateTime } from '../lib/format';
import { cn } from './ui';
import { verkleinern } from '../lib/bilder';

/**
 * Zustandsfotos eines Geräts.
 *
 * Wichtig bei Rücknahme, Mitarbeiterverkauf und Verschrottung: Später lässt
 * sich nicht mehr rekonstruieren, in welchem Zustand ein Gerät zurückkam.
 * Am Telefon öffnet der Knopf direkt die Kamera.
 */

interface Photo {
  id: number;
  filename: string;
  mime: string;
  size: number;
  note: string | null;
  movement_id: number | null;
  created_at: string;
  url: string;
}

export function PhotoSection({ assetId }: { assetId: number }) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const kameraRef = useRef<HTMLInputElement>(null);
  const dateiRef = useRef<HTMLInputElement>(null);
  const [notiz, setNotiz] = useState('');
  const [gross, setGross] = useState<Photo | null>(null);

  const photos = useQuery({
    queryKey: ['asset-photos', assetId],
    queryFn: () => api<Photo[]>(`/assets/${assetId}/photos`),
  });

  const hochladen = useMutation({
    mutationFn: async (datei: File) => {
      const klein = await verkleinern(datei);
      const daten = new FormData();
      if (notiz.trim()) daten.append('note', notiz.trim());
      daten.append('file', klein, klein.name);

      const antwort = await fetch(`${API_BASE}/assets/${assetId}/photos`, {
        method: 'POST',
        body: daten,
        credentials: 'include',
      });
      if (!antwort.ok) {
        const fehler = await antwort.json().catch(() => null);
        throw new ApiError(
          antwort.status,
          'UPLOAD_FAILED',
          fehler?.error?.message ?? 'Das Bild konnte nicht gespeichert werden',
        );
      }
      return antwort.json() as Promise<Photo>;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['asset-photos', assetId] });
      setNotiz('');
      toast.success('Foto hinterlegt');
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Hochladen fehlgeschlagen'),
  });

  const entfernen = useMutation({
    mutationFn: (id: number) => api(`/photos/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['asset-photos', assetId] });
      setGross(null);
      toast.success('Foto entfernt');
    },
  });

  const darfPflegen = auth.can('asset.write');
  const bilder = photos.data ?? [];

  return (
    <section className="surface animate-fade-up pad">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-[family-name:var(--font-display)] text-2xl">Zustand</h2>
          <p className="mt-1 text-sm text-[var(--color-fg3)]">
            Fotos für Rücknahme, Verkauf und Verschrottung — später ist der Zustand nicht mehr
            rekonstruierbar.
          </p>
        </div>

        {darfPflegen && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => kameraRef.current?.click()}
              className="btn btn-accent btn-md"
              disabled={hochladen.isPending}
            >
              <Camera className="h-4 w-4" aria-hidden />
              Aufnehmen
            </button>
            <button
              type="button"
              onClick={() => dateiRef.current?.click()}
              className="btn btn-ghost btn-md"
              disabled={hochladen.isPending}
            >
              <ImagePlus className="h-4 w-4" aria-hidden />
              Auswählen
            </button>
          </div>
        )}
      </div>

      {darfPflegen && (
        <>
          {/* `capture` öffnet am Telefon unmittelbar die Rückkamera. */}
          <input
            ref={kameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(event) => {
              const datei = event.target.files?.[0];
              if (datei) hochladen.mutate(datei);
              event.target.value = '';
            }}
          />
          <input
            ref={dateiRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(event) => {
              for (const datei of Array.from(event.target.files ?? [])) hochladen.mutate(datei);
              event.target.value = '';
            }}
          />

          <input
            className="field mb-4"
            placeholder="Notiz zum nächsten Foto, z. B. „Displaybruch unten rechts“"
            value={notiz}
            onChange={(event) => setNotiz(event.target.value)}
            aria-label="Notiz zum Foto"
          />
        </>
      )}

      {hochladen.isPending && (
        <p className="mb-3 text-sm text-[var(--color-fg3)]">Bild wird verkleinert und gespeichert …</p>
      )}

      {bilder.length === 0 ? (
        <p className="rounded-[var(--radius-md)] bg-[var(--color-bg-tinted)] px-4 py-6 text-center text-[var(--color-fg3)]">
          Noch keine Fotos hinterlegt.
        </p>
      ) : (
        <ul className="stagger grid grid-cols-2 gap-3 sm:grid-cols-3">
          {bilder.map((foto) => (
            <li key={foto.id}>
              <button
                type="button"
                onClick={() => setGross(foto)}
                className="card-link block w-full overflow-hidden rounded-[var(--radius-md)] border border-[var(--color-line)] bg-[var(--color-bg-tinted)] text-left"
              >
                <img
                  src={foto.url}
                  alt={foto.note ?? `Zustandsfoto vom ${formatDateTime(foto.created_at)}`}
                  loading="lazy"
                  className="aspect-square w-full object-cover"
                />
                <span className="block px-2.5 py-2">
                  <span className="block truncate text-sm text-[var(--color-fg1)]">
                    {foto.note ?? 'ohne Notiz'}
                  </span>
                  <span className="block text-sm text-[var(--color-fg3)]">
                    {formatDateTime(foto.created_at)}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Großansicht */}
      {gross && (
        <div
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-[var(--color-khadi-forest-900)]/90 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Foto in groß"
          onClick={(event) => {
            if (event.target === event.currentTarget) setGross(null);
          }}
        >
          <img
            src={gross.url}
            alt={gross.note ?? 'Zustandsfoto'}
            className="max-h-[80dvh] max-w-full rounded-[var(--radius-lg)] object-contain"
          />
          <div className="mt-4 flex items-center gap-3 text-[var(--color-khadi-cream-50)]">
            <span className="text-sm">
              {gross.note ? `${gross.note} · ` : ''}
              {formatDateTime(gross.created_at)}
            </span>
            {darfPflegen && (
              <button
                type="button"
                onClick={() => entfernen.mutate(gross.id)}
                className={cn('btn btn-sm', 'bg-[var(--color-accent)] text-white')}
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
                Entfernen
              </button>
            )}
            <button
              type="button"
              onClick={() => setGross(null)}
              className="btn btn-sm bg-[var(--color-khadi-cream-50)] text-[var(--color-fg)]"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
              Schließen
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
